"use client";

import { useEffect, useRef } from "react";
import {
  type ActiveChip,
  type FilterControllerProps,
  type FilterMenuItem,
  AppSearchInteractionOption,
  SingleSelectFilterMenu,
  useEmitAppSearchInteractionEvent,
  useNamespacedTranslations,
  useRegisterFilter,
  useSingleValueParameter,
} from "@zapier/shared";

type VersionValue = "latest_public" | "other";

const VALID_VERSIONS = new Set<VersionValue>(["latest_public", "other"]);
const ICON = "navApps";

const CHIP_KEY_BY_VALUE: Record<VersionValue, string> = {
  latest_public: "latestPublic",
  other: "other",
};

const ANALYTICS_OPTION_BY_VALUE: Record<
  VersionValue,
  AppSearchInteractionOption
> = {
  latest_public: AppSearchInteractionOption.LatestPublic,
  other: AppSearchInteractionOption.Other,
};

// Apps view's app-version filter. Single-select on the `shared` URL param,
// mirroring the legacy `SharingFilter` (data-source toggle: public vs other).
export function AppsVersionFilterController({
  isOpen,
  namespace,
  onClose,
  onInteraction,
}: FilterControllerProps): JSX.Element | null {
  const t = useNamespacedTranslations(namespace);
  const shared = useSingleValueParameter<VersionValue>("shared", {
    validValues: VALID_VERSIONS,
  });
  const { emit } = useEmitAppSearchInteractionEvent();
  // Chip renders only when `?shared=` is set; when missing, the data layer
  // shows all app versions (public + private merged), so "Clear all" widens
  // the visible result set.
  const hasValue = !!shared.value;
  const value: VersionValue = shared.value ?? "latest_public";

  // Defer close until the URL value changes.
  const closeOnNextValue = useRef(false);
  useEffect(() => {
    if (closeOnNextValue.current) {
      closeOnNextValue.current = false;
      onClose();
    }
  }, [shared.value, onClose]);

  const chipKey = CHIP_KEY_BY_VALUE[value];
  const label = t(`version.chipLabel.${chipKey}`);
  const ariaLabel = t(`version.chipAriaLabel.${chipKey}`);

  let chip: ActiveChip | undefined;
  let signature = "version:none";
  if (hasValue) {
    chip = {
      key: "version",
      iconName: ICON,
      title: t("version.label"),
      label,
      ariaLabel,
    };
    signature = `version:${value}`;
  } else if (isOpen) {
    chip = {
      key: "version",
      iconName: ICON,
      title: t("version.label"),
      ariaLabel: t("version.label"),
      isDraft: true,
    };
    signature = "version:draft";
  }

  const items: FilterMenuItem[] = [
    { id: "latest_public", name: t("version.latestPublic") },
    { id: "other", name: t("version.other") },
  ];

  useRegisterFilter({
    type: "version",
    hasValue,
    signature,
    chip,
    renderMenu: () => (
      <SingleSelectFilterMenu
        label={t("version.label")}
        items={items}
        selectedId={value}
        onSelect={(id) => {
          if (!VALID_VERSIONS.has(id as VersionValue)) {
            onClose();
            return;
          }
          const picked = id as VersionValue;
          if (picked === value) {
            onClose();
            return;
          }
          closeOnNextValue.current = true;
          shared.set(picked);
          emit(ANALYTICS_OPTION_BY_VALUE[picked]);
          onInteraction?.();
        }}
      />
    ),
  });
  // eslint-disable-next-line unicorn/no-null -- headless controller renders nothing
  return null;
}
